import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, FileText, Shield, Wrench, ChevronDown, ChevronUp, Filter } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

interface Props {
  baProjectId: string | null;
}

const RISK_STYLES: Record<string, string> = {
  critical: "bg-destructive/15 text-destructive border-destructive/30",
  high: "bg-orange-100 text-orange-700 border-orange-300",
  medium: "bg-yellow-100 text-yellow-700 border-yellow-300",
  low: "bg-green-100 text-green-700 border-green-300",
};

const PRIORITY_STYLES: Record<string, string> = {
  must: "bg-destructive/15 text-destructive border-destructive/30",
  should: "bg-accent/15 text-accent border-accent/30",
  could: "bg-secondary text-secondary-foreground border-border",
  wont: "bg-muted text-muted-foreground border-border",
};

function CoverageIcon({ has, label, count }: { has: boolean; label: string; count: number }) {
  return (
    <span className={`flex items-center gap-1 text-[11px] font-mono ${has ? "text-green-600" : "text-muted-foreground/50"}`}>
      {has ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {count} {label}
    </span>
  );
}

export default function TraceabilityMatrix({ baProjectId }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [matrix, setMatrix] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [filterDomain, setFilterDomain] = useState("all");
  const [filterGap, setFilterGap] = useState("all");

  useEffect(() => { if (baProjectId) load(); }, [baProjectId]);

  const load = async () => {
    if (!baProjectId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/api/tools/projects/${baProjectId}/traceability`, { headers: authHeader });
      setMatrix(data.matrix || []);
    } catch { setMatrix([]); }
    finally { setLoading(false); }
  };

  const toggle = (id: string) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const domains = Array.from(new Set(matrix.map(r => r.domain).filter(Boolean))).sort();

  const filtered = matrix.filter(req => {
    if (filterDomain !== "all" && req.domain !== filterDomain) return false;
    if (filterGap === "no-tests" && req.coverage.has_tests) return false;
    if (filterGap === "no-compliance" && req.coverage.has_compliance) return false;
    if (filterGap === "open-capa" && !req.coverage.has_capa) return false;
    return true;
  });

  const total = matrix.length;
  const withTests = matrix.filter(r => r.coverage.has_tests).length;
  const withCompliance = matrix.filter(r => r.coverage.has_compliance).length;
  const withCapa = matrix.filter(r => r.coverage.has_capa).length;

  const pct = (n: number) => total > 0 ? Math.round((n / total) * 100) : 0;

  if (!baProjectId) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
        Visit Requirements tab first to initialise the project backend.
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div>
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <FileText className="h-4 w-4" /> Traceability Matrix
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">Requirement → Test Cases → Compliance → CAPA (ISO 13485 §7.3)</p>
      </div>

      {/* Summary bar */}
      {!loading && total > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: "Requirements", value: total, sub: "total", color: "text-foreground" },
            { label: "Test Coverage", value: `${pct(withTests)}%`, sub: `${withTests}/${total} covered`, color: withTests === total ? "text-green-600" : "text-orange-600" },
            { label: "Compliance Checked", value: `${pct(withCompliance)}%`, sub: `${withCompliance}/${total} checked`, color: withCompliance === total ? "text-green-600" : "text-orange-600" },
            { label: "Open CAPAs", value: withCapa, sub: "requirements with CAPA", color: withCapa > 0 ? "text-destructive" : "text-green-600" },
          ].map(s => (
            <div key={s.label} className="border border-border rounded-md bg-card p-3">
              <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">{s.label}</p>
              <p className={`text-xl font-bold mt-0.5 ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-muted-foreground">{s.sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <Filter className="h-3.5 w-3.5 text-muted-foreground" />
        <select value={filterDomain} onChange={e => setFilterDomain(e.target.value)} className="h-7 text-xs border border-input rounded-md px-2 bg-background">
          <option value="all">All domains</option>
          {domains.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filterGap} onChange={e => setFilterGap(e.target.value)} className="h-7 text-xs border border-input rounded-md px-2 bg-background">
          <option value="all">All coverage states</option>
          <option value="no-tests">Missing tests</option>
          <option value="no-compliance">Not compliance-checked</option>
          <option value="open-capa">Has open CAPA</option>
        </select>
        {(filterDomain !== "all" || filterGap !== "all") && (
          <button onClick={() => { setFilterDomain("all"); setFilterGap("all"); }} className="text-[11px] text-accent underline hover:no-underline">Clear</button>
        )}
      </div>

      {loading && <p className="text-xs text-muted-foreground animate-pulse">Loading traceability data…</p>}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 border border-border rounded-md bg-secondary/10">
          <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
          <p className="text-sm font-semibold">{total === 0 ? "No requirements yet" : "No requirements match filters"}</p>
          <p className="text-xs text-muted-foreground mt-1">{total === 0 ? "Create requirements in the Requirements tab first." : "Try adjusting the filters above."}</p>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map(req => {
          const isOpen = expanded[req.id];
          return (
            <div key={req.id} className={`border rounded-md bg-card overflow-hidden ${req.risk_level === "critical" ? "border-destructive/40" : req.risk_level === "high" ? "border-orange-300" : "border-border"}`}>
              <div
                className="p-3 cursor-pointer hover:bg-secondary/20 transition-colors"
                onClick={() => toggle(req.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-card-foreground">{req.title}</span>
                      {req.req_type && <Badge variant="outline" className="text-[10px] px-1.5">{req.req_type}</Badge>}
                      {req.priority && <Badge variant="outline" className={`text-[10px] px-1.5 ${PRIORITY_STYLES[req.priority] || ""}`}>{req.priority}</Badge>}
                      {req.risk_level && <Badge variant="outline" className={`text-[10px] px-1.5 ${RISK_STYLES[req.risk_level] || ""}`}>⚠ {req.risk_level} risk</Badge>}
                      {req.domain && <span className="text-[10px] font-mono text-muted-foreground">{req.domain}</span>}
                    </div>

                    <div className="flex gap-4 mt-1.5 flex-wrap">
                      <CoverageIcon has={req.coverage.has_tests} label="tests" count={req.test_cases.length} />
                      <CoverageIcon has={req.coverage.has_compliance} label="compliance" count={req.compliance_results.length} />
                      <CoverageIcon has={req.coverage.has_capa} label="CAPAs" count={req.capa_records.length} />
                    </div>
                  </div>
                  <div className="shrink-0 text-muted-foreground">
                    {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-border p-3 grid grid-cols-1 md:grid-cols-3 gap-3 bg-secondary/10">
                  {/* Test Cases */}
                  <div>
                    <p className="text-[10px] font-mono font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-1.5">
                      <FileText className="h-3 w-3" /> Test Cases
                    </p>
                    {req.test_cases.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground italic">None linked</p>
                    ) : req.test_cases.map((tc: any) => (
                      <div key={tc.id} className="text-[11px] text-foreground mb-1">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${tc.status === "approved" ? "bg-green-500" : tc.status === "review" ? "bg-blue-500" : "bg-muted-foreground/40"}`} />
                        {tc.title}
                        {tc.feature_tag && <span className="text-muted-foreground ml-1">· {tc.feature_tag}</span>}
                      </div>
                    ))}
                  </div>

                  {/* Compliance */}
                  <div>
                    <p className="text-[10px] font-mono font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-1.5">
                      <Shield className="h-3 w-3" /> Compliance
                    </p>
                    {req.compliance_results.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground italic">Not checked</p>
                    ) : req.compliance_results.map((cr: any) => (
                      <div key={cr.id} className="text-[11px] mb-1">
                        <span className={`font-medium ${(cr.violations || []).length > 0 ? "text-destructive" : "text-green-600"}`}>
                          {(cr.violations || []).length > 0 ? `${cr.violations.length} violation${cr.violations.length > 1 ? "s" : ""}` : "Pass"}
                        </span>
                        {(cr.warnings || []).length > 0 && <span className="text-yellow-600 ml-1">· {cr.warnings.length} warn</span>}
                        <span className="text-muted-foreground ml-1">· {new Date(cr.created_at || "").toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>

                  {/* CAPA */}
                  <div>
                    <p className="text-[10px] font-mono font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-1.5">
                      <Wrench className="h-3 w-3" /> CAPA
                    </p>
                    {req.capa_records.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground italic">No CAPAs</p>
                    ) : req.capa_records.map((ca: any) => (
                      <div key={ca.id} className="text-[11px] mb-1">
                        <span className={`font-medium ${ca.status === "closed" ? "text-green-600" : ca.status === "in-review" ? "text-blue-600" : "text-yellow-600"}`}>
                          {ca.status}
                        </span>
                        <span className="text-muted-foreground ml-1">· {ca.nc_description?.slice(0, 40)}{ca.nc_description?.length > 40 ? "…" : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
